import type { UserAuthDto } from '@procureflow/shared-types';

/**
 * Auth client — ADR-016 cookie transport.
 *
 * The session is owned by the backend via three cookies:
 *   - pf_access   (HttpOnly, sent on every /api request)
 *   - pf_refresh  (HttpOnly, sent only on /api/auth/refresh)
 *   - csrf_token  (JS-readable; FE echoes it as X-CSRF-Token on
 *                  mutating cookie-auth requests)
 *
 * The FE no longer persists tokens to localStorage. Identity is
 * discovered via GET /api/me on app boot; the in-memory store tracks
 * the resolved user so React components can read it synchronously.
 *
 * Wave D.2.b retired the legacy `pf_session` localStorage hint —
 * bootstrap is cookie-only. Body-token Bearer augmentation on
 * fetchWithAuth is the remaining dual-transport carrier and is the
 * scope of Wave D.4.
 */
export interface AuthSession {
  user: UserAuthDto;
}

interface AuthError extends Error {
  code: string;
}

const CSRF_COOKIE = 'csrf_token';

type SessionListener = (s: AuthSession | null) => void;
const listeners = new Set<SessionListener>();

// In-memory store. AuthProvider populates this from /api/me on mount and
// from the login response. fetchWithAuth reads it for the legacy Bearer
// pathway (cookies don't need it).
let currentSession: AuthSession | null = null;

let expirationPending = false;

export function getSession(): AuthSession | null {
  return currentSession;
}

export function setSession(s: AuthSession | null): void {
  currentSession = s;
  for (const l of listeners) l(s);
}

export function subscribeSession(l: SessionListener): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

export function consumeExpirationFlag(): boolean {
  const v = expirationPending;
  expirationPending = false;
  return v;
}

export class SessionExpiredError extends Error {
  constructor(message = 'Session expired.') {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

async function toAuthError(res: Response): Promise<AuthError> {
  try {
    const body = await res.json();
    const err = new Error(body.message ?? 'An unexpected error occurred.') as AuthError;
    err.code = body.code ?? 'UNKNOWN_ERROR';
    return err;
  } catch {
    const err = new Error('An unexpected error occurred.') as AuthError;
    err.code = 'UNKNOWN_ERROR';
    return err;
  }
}

function readCsrfCookie(): string | null {
  // csrf_token is intentionally NOT HttpOnly per ADR-016 sub-decision #7
  // so the FE can read it and echo it as a request header.
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  for (const part of cookies) {
    const trimmed = part.trim();
    if (trimmed.startsWith(`${CSRF_COOKIE}=`)) {
      return trimmed.slice(CSRF_COOKIE.length + 1);
    }
  }
  return null;
}

/**
 * Bootstrap the session by calling /api/me.
 *
 * Browser auto-attaches the pf_access cookie via credentials: 'include'.
 * On 401, attempt a single /api/auth/refresh (browser auto-attaches
 * pf_refresh) and retry /api/me. If both attempts fail and the cookie
 * jar was carrying a session (csrf_token signal), set the expiration
 * flag so AuthProvider redirects to /login with the session_expired
 * banner.
 *
 * If no session existed at all AND /api/me returns 401, this is just a
 * fresh-tab unauthenticated state — no expiration flag, no banner.
 */
export async function bootstrapSession(): Promise<AuthSession | null> {
  // csrf_token is JS-readable and cleared by logout, so its presence
  // means the browser is carrying a live cookie session whose access
  // cookie we cannot read (HttpOnly). Used to fire the session_expired
  // banner when a stale cookie session fails to refresh.
  const hadCookieSession = readCsrfCookie() !== null;
  try {
    let res = await meRequest();
    if (res.status === 401) {
      const refreshed = await tryBootstrapRefresh();
      if (refreshed) {
        res = await meRequest();
      } else if (hadCookieSession) {
        // The user had a cookie session but refresh failed. Surface
        // the session_expired banner.
        expirationPending = true;
        setSession(null);
        return null;
      }
    }
    if (!res.ok) {
      setSession(null);
      return null;
    }
    const body = await res.json();
    const session: AuthSession = {
      user: body.data as UserAuthDto,
    };
    setSession(session);
    return session;
  } catch {
    setSession(null);
    return null;
  }
}

async function meRequest(): Promise<Response> {
  return fetch('/api/me', { credentials: 'include' });
}

async function tryBootstrapRefresh(): Promise<boolean> {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const csrf = readCsrfCookie();
    if (csrf) headers['X-CSRF-Token'] = csrf;
    const r = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers,
      credentials: 'include',
      body: '{}',
    });
    return r.ok;
  } catch {
    return false;
  }
}

export async function loginApi(email: string, password: string): Promise<AuthSession> {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
    credentials: 'include',
  });
  if (!res.ok) throw await toAuthError(res);
  const { data } = await res.json();
  // ADR-016 Wave D.4.3: cookies are the only auth transport. The
  // browser stored pf_access + pf_refresh + csrf_token from Set-Cookie
  // headers; the body carries only the user envelope.
  return { user: data.user as UserAuthDto };
}

export async function logoutApi(): Promise<void> {
  // Best-effort: caller must clear local session regardless of response.
  // CSRF header echoed when the cookie is present; logout is a mutating
  // request so the middleware requires it under the cookie path.
  const headers: Record<string, string> = {};
  const csrf = readCsrfCookie();
  if (csrf) headers['X-CSRF-Token'] = csrf;
  await fetch('/api/auth/logout', {
    method: 'POST',
    headers,
    credentials: 'include',
  }).catch(() => undefined);
}

let inFlightRefresh: Promise<void> | null = null;

async function performRefresh(): Promise<void> {
  if (inFlightRefresh) return inFlightRefresh;
  inFlightRefresh = (async () => {
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const csrf = readCsrfCookie();
      if (csrf) headers['X-CSRF-Token'] = csrf;
      const r = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers,
        credentials: 'include',
        body: '{}',
      });
      if (!r.ok) throw new Error('refresh failed');
      // The backend rotated cookies via Set-Cookie; nothing to mirror
      // into the in-memory session under ADR-016 Wave D.4 (cookies-only).
    } finally {
      inFlightRefresh = null;
    }
  })();
  return inFlightRefresh;
}

function withCookieAuth(init: RequestInit | undefined): RequestInit {
  const headers = new Headers(init?.headers);
  const csrf = readCsrfCookie();
  const method = (init?.method ?? 'GET').toUpperCase();
  if (csrf && method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
    headers.set('X-CSRF-Token', csrf);
  }
  return { ...init, headers, credentials: 'include' };
}

/**
 * Authenticated fetch — cookies are the only auth transport
 * (ADR-016 Wave D.4).
 *
 * On a 401 the wrapper attempts one /api/auth/refresh and replays the
 * original request. Refresh failure surfaces SessionExpiredError; the
 * AuthContext subscriber reads the expiration flag and redirects to
 * /login?reason=session_expired.
 */
export async function fetchWithAuth(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  // In dev, MSW activates asynchronously. fetchWithAuth is the canonical
  // authenticated-fetch path, so the mswReady await belongs here.
  if (import.meta.env.DEV) {
    const { mswReady } = await import('@/mocks/browser');
    await mswReady;
  }

  // Cookies are auto-attached via credentials: 'include'.
  let res = await fetch(input, withCookieAuth(init));
  if (res.status !== 401) return res;

  try {
    await performRefresh();
  } catch {
    expirationPending = true;
    setSession(null);
    throw new SessionExpiredError();
  }

  // Re-read csrf after rotation; cookies were updated by Set-Cookie.
  res = await fetch(input, withCookieAuth(init));
  return res;
}
